import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaShieldAlt,
  FaLock,
  FaUserShield,
  FaDatabase,
  FaExchangeAlt,
  FaEye,
  FaTrashAlt
} from "react-icons/fa";

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 pt-20 md:pt-24 pb-12">
      <div className="w-full max-w-[1180px] mx-auto px-4 md:px-6">
        {/* Back Button */}
        <div className="mb-3 md:mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-primary hover:text-primary-dark transition-colors font-medium group"
          >
            <FaArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" />
            Go Back
          </button>
        </div>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-5 md:p-8 mb-4 md:mb-8">
          <div className="flex items-center mb-4">
            <FaShieldAlt className="text-primary text-3xl mr-4" />
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-gray-800">
                Privacy Policy
              </h1>
              <p className="text-gray-600 text-base md:text-lg mt-1 md:mt-2">
                Your privacy is important to us. This policy explains how we collect,
                use, and protect your personal information.
              </p>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            Last updated: 15 Aug 2025
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-5 md:p-8">
          {/* Section 1 */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaDatabase className="text-blue-500 mr-3" />
              1. Information We Collect
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We collect information to provide better services to our users.
              The information we collect includes:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <FaUserShield className="text-blue-600 mr-2" />
                  Personal Information
                </h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• Full name and contact details</li>
                  <li>• Email address and phone number</li>
                  <li>• Profile photo (optional)</li>
                  <li>• Emergency contact information</li>
                  <li>• Government ID details (for drivers)</li>
                </ul>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <FaLock className="text-green-600 mr-2" />
                  Service Data
                </h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• Pickup and drop-off locations</li>
                  <li>• Trip history and preferences</li>
                  <li>• Payment information</li>
                  <li>• Ratings and feedback</li>
                  <li>• Device and usage information</li>
                </ul>
              </div>
            </div>

            <div className="mt-4 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h3 className="font-semibold text-gray-800 mb-2">Location Information</h3>
              <p className="text-gray-700 text-sm">
                We collect precise location data when you use our services to enable
                ride matching, navigation, and safety features. You can control
                location sharing through your device settings.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaExchangeAlt className="text-purple-500 mr-3" />
              2. How We Use Your Information
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Your information is used to provide and improve our services:
            </p>

            <div className="space-y-4">
              <div className="border-l-4 border-primary pl-4">
                <h3 className="font-semibold text-gray-800 mb-2">Service Provision</h3>
                <ul className="space-y-1 text-gray-700 text-sm">
                  <li>• Facilitate ride bookings and payments</li>
                  <li>• Connect passengers with nearby drivers</li>
                  <li>• Provide real-time tracking and navigation</li>
                  <li>• Process payments and generate receipts</li>
                </ul>
              </div>

              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="font-semibold text-gray-800 mb-2">Safety & Security</h3>
                <ul className="space-y-1 text-gray-700 text-sm">
                  <li>• Verify driver identity and credentials</li>
                  <li>• Monitor trips for safety purposes</li>
                  <li>• Investigate incidents and disputes</li>
                  <li>• Prevent fraud and unauthorized access</li>
                </ul>
              </div>

              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-semibold text-gray-800 mb-2">Communication</h3>
                <ul className="space-y-1 text-gray-700 text-sm">
                  <li>• Send booking confirmations and updates</li>
                  <li>• Provide customer support</li>
                  <li>• Share important service announcements</li>
                  <li>• Collect feedback and ratings</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaEye className="text-orange-500 mr-3" />
              3. Information Sharing
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not sell your personal information. Limited sharing occurs only
              in the following circumstances:
            </p>

            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">With Drivers/Passengers</h3>
                <p className="text-gray-700 text-sm">
                  We share necessary information (name, pickup location, contact details)
                  between passengers and drivers to complete rides safely.
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">Service Providers</h3>
                <p className="text-gray-700 text-sm">
                  We work with trusted third parties for payment processing, mapping
                  services, and customer support under strict confidentiality agreements.
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">Legal Requirements</h3>
                <p className="text-gray-700 text-sm">
                  We may disclose information when required by law, court order, or
                  to protect the rights, property, or safety of CabZigo and our users.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaLock className="text-red-500 mr-3" />
              4. Data Security
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We implement robust security measures to protect your personal information:
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="bg-green-100 p-2 rounded-full mr-3 mt-1">
                    <FaLock className="text-green-600 text-sm" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">Encryption</h4>
                    <p className="text-gray-600 text-xs">
                      All data transmission is encrypted using industry-standard TLS
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-blue-100 p-2 rounded-full mr-3 mt-1">
                    <FaShieldAlt className="text-blue-600 text-sm" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">Secure Storage</h4>
                    <p className="text-gray-600 text-xs">
                      Data is stored in secure, access-controlled environments
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="bg-purple-100 p-2 rounded-full mr-3 mt-1">
                    <FaUserShield className="text-purple-600 text-sm" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">Access Control</h4>
                    <p className="text-gray-600 text-xs">
                      Limited access to authorized personnel only
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-orange-100 p-2 rounded-full mr-3 mt-1">
                    <FaDatabase className="text-orange-600 text-sm" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">Regular Audits</h4>
                    <p className="text-gray-600 text-xs">
                      Regular security assessments and vulnerability testing
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 flex items-center">
              <FaUserShield className="text-green-500 mr-3" />
              5. Your Rights and Choices
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You have several rights regarding your personal data:
            </p>

            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">Access Your Data</h3>
                <p className="text-gray-700 text-sm">
                  You can request a copy of the personal information we hold about you
                  through your account settings or by contacting us.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">Correct Information</h3>
                <p className="text-gray-700 text-sm">
                  You can update your personal information anytime through your profile
                  settings or by contacting our support team.
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">Control Communications</h3>
                <p className="text-gray-700 text-sm">
                  You can opt out of promotional emails and customize notification
                  preferences in your account settings.
                </p>
              </div>

              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                  <FaTrashAlt className="text-red-500 mr-2" />
                  Delete Your Account
                </h3>
                <p className="text-gray-700 text-sm">
                  You can request deletion of your account and associated data.
                  Some information may be retained for legal or safety purposes.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4">
              6. Data Retention
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We retain your information only as long as necessary to provide our
              services and comply with legal obligations:
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left p-3 font-semibold">Data Type</th>
                    <th className="text-left p-3 font-semibold">Retention Period</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="p-3">Account Information</td>
                    <td className="p-3">Until account deletion</td>
                  </tr>
                  <tr>
                    <td className="p-3">Trip History</td>
                    <td className="p-3">3 years for tax purposes</td>
                  </tr>
                  <tr>
                    <td className="p-3">Payment Data</td>
                    <td className="p-3">7 years for financial records</td>
                  </tr>
                  <tr>
                    <td className="p-3">Location Data</td>
                    <td className="p-3">30 days after trip completion</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 7 */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4">
              7. Cookies and Tracking
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use cookies and similar technologies to improve your experience:
            </p>

            <div className="space-y-3">
              <div className="flex items-start">
                <span className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-xs mr-3 mt-1">
                  1
                </span>
                <div>
                  <strong className="text-gray-800">Essential Cookies:</strong>
                  <span className="text-gray-700"> Required for basic website functionality</span>
                </div>
              </div>

              <div className="flex items-start">
                <span className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-xs mr-3 mt-1">
                  2
                </span>
                <div>
                  <strong className="text-gray-800">Performance Cookies:</strong>
                  <span className="text-gray-700"> Help us understand how you use our services</span>
                </div>
              </div>

              <div className="flex items-start">
                <span className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-xs mr-3 mt-1">
                  3
                </span>
                <div>
                  <strong className="text-gray-800">Preference Cookies:</strong>
                  <span className="text-gray-700"> Remember your settings and preferences</span>
                </div>
              </div>
            </div>
          </section>

          {/* Section 8 */}
          <section className="mb-6 md:mb-8">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4">
              8. Children's Privacy
            </h2>
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <p className="text-gray-700">
                CabZigo is not intended for children under 18 years of age.
                We do not knowingly collect personal information from children.
                If you believe we have collected information from a child, please
                contact us immediately.
              </p>
            </div>
          </section>

          {/* Contact Section */}
          <section className="bg-gray-50 p-4 md:p-6 rounded-lg">
            <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4">
              Contact Us
            </h2>
            <p className="text-gray-700 mb-4">
              If you have any questions about this Privacy Policy or our data
              practices, please contact us:
            </p>
            <div className="space-y-2 text-gray-700">
              <p>
                <strong>Email:</strong> info@rajeshwarinfotech.com
              </p>
              <p>
                <strong>Phone:</strong> +91 7096386636
              </p>
              <p>
                <strong>Address:</strong> Data Protection Officer, Rajkot, Gujarat, India
              </p>
            </div>
          </section>
        </div>

        {/* Related Links */}
        <div className="mt-4 md:mt-8 text-center">
          <div className="inline-flex space-x-4">
            <Link
              to="/terms"
              className="text-primary hover:text-primary-dark underline"
            >
              Terms & Conditions
            </Link>
            <Link
              to="/refund"
              className="text-primary hover:text-primary-dark underline"
            >
              Refund Policy
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
